import React, { useState } from 'react';
import axios from 'axios';
import { Upload, X, Video, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to convert YouTube URL to embed format
const convertYouTubeUrl = (url) => {
  if (!url) return '';
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return url;
};

export const VideoUploader = ({ onVideoUploaded, initialType = 'youtube', initialUrl = '' }) => {
  const [videoType, setVideoType] = useState(initialType);
  const [youtubeUrl, setYoutubeUrl] = useState(initialType === 'youtube' ? initialUrl : '');
  const [uploadedUrl, setUploadedUrl] = useState(initialType === 'uploaded' ? initialUrl : '');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // Validate file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video file is too large (max 100MB)');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('crm_token');
      const response = await axios.post(`${API}/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setUploadedUrl(response.data.url);
      onVideoUploaded('uploaded', response.data.url);
      toast.success('Video uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload video');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleYoutubeSubmit = () => {
    if (!youtubeUrl) {
      toast.error('Please enter a YouTube URL');
      return;
    }
    const embedUrl = convertYouTubeUrl(youtubeUrl);
    onVideoUploaded('youtube', embedUrl);
    toast.success('YouTube video added!');
  };

  const clearVideo = () => {
    setUploadedUrl('');
    setYoutubeUrl('');
    onVideoUploaded(videoType, '');
  };

  return (
    <div className="space-y-4">
      {/* Type Selector */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={videoType === 'youtube' ? 'default' : 'outline'}
          onClick={() => { setVideoType('youtube'); clearVideo(); }}
          className={videoType === 'youtube' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          <Youtube className="w-4 h-4 mr-2" /> YouTube URL
        </Button>
        <Button
          type="button"
          variant={videoType === 'uploaded' ? 'default' : 'outline'}
          onClick={() => { setVideoType('uploaded'); clearVideo(); }}
          className={videoType === 'uploaded' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          <Upload className="w-4 h-4 mr-2" /> Upload from Computer
        </Button>
      </div>

      {/* YouTube Input */}
      {videoType === 'youtube' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <label className="text-sm font-medium text-red-800 mb-2 block flex items-center gap-2">
            <Youtube className="w-4 h-4" /> YouTube Video URL
          </label>
          <div className="flex gap-2">
            <Input
              placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="bg-white flex-1"
            />
            <Button type="button" onClick={handleYoutubeSubmit} className="bg-red-600 hover:bg-red-700">
              Add
            </Button>
          </div>
          <p className="text-xs text-red-600 mt-1">Paste any YouTube URL - it will be auto-converted</p>
          
          {youtubeUrl && (
            <div className="mt-3 aspect-video bg-stone-900 rounded overflow-hidden relative">
              <iframe
                src={convertYouTubeUrl(youtubeUrl)}
                title="Video Preview"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
              <button
                type="button"
                onClick={clearVideo}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Upload from Computer */}
      {videoType === 'uploaded' && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="text-sm font-medium text-blue-800 mb-2 block flex items-center gap-2">
            <Video className="w-4 h-4" /> Upload Video from Computer
          </label>
          
          {!uploadedUrl ? (
            <div className="border-2 border-dashed border-blue-300 rounded p-6 text-center hover:border-blue-500 transition-colors bg-white">
              <input
                type="file"
                accept="video/*"
                onChange={handleFileChange}
                className="hidden"
                id="video-upload"
                disabled={uploading}
              />
              <label
                htmlFor="video-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                {uploading ? (
                  <>
                    <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-blue-700 font-medium">Uploading... {uploadProgress}%</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-blue-400" />
                    <p className="font-medium text-blue-900">Click to upload video</p>
                    <p className="text-sm text-blue-600">MP4, WebM, MOV (max 100MB)</p>
                  </>
                )}
              </label>
            </div>
          ) : (
            <div className="aspect-video bg-stone-900 rounded overflow-hidden relative">
              <video
                src={uploadedUrl}
                className="w-full h-full object-cover"
                controls
              />
              <button
                type="button"
                onClick={clearVideo}
                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoUploader;
